import {
  HStack,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LearningPath } from "../../models/LearningPath";
import { Page } from "../../models/Paged";
import { Quiz } from "../../models/Quiz";
import { StudentTableItem } from "../../models/StudentTableItem";
import ErrorSection from "../ErrorSection";
import ExerciseProgressItem from "../items/ExerciseProgressItem";
import QuizProgressItem from "../items/QuizProgressItem";
import NoItemsCard from "../NoItemsCard";
import Pagination from "../Pagination";
import { Lesson } from "../../models/Lesson";

type Props = {
  learningPath: LearningPath;
  progresses: StudentTableItem[];
  isLoading?: boolean;
  isError?: boolean;
  refetch: (page: { page: number }) => void;
  pagination?: Page;
};

export default function LearningPathStudentsProgressesTable({
  learningPath,
  progresses,
  isLoading,
  isError,
  pagination,
  refetch,
}: Props) {
  const { t } = useTranslation();

  const modules = useMemo(
    () =>
      learningPath.lessons.reduce<{ [key: string]: Lesson[] }>(
        (p, c) => ({ ...p, [c.module.name]: [...(p[c.module.name] ?? []), c] }),
        {},
      ),
    [learningPath.lessons],
  );

  useEffect(() => {
    refetch?.({ page: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLearningPathLessons = useCallback(
    (student: StudentTableItem) => {
      return Object.values(modules)
        .flat()
        .map((lesson) => {
          const { preTasks, postTasks } = lesson.quizzes?.reduce<{
            preTasks: Quiz[];
            postTasks: Quiz[];
          }>(
            (p, c) => {
              p[c.schedule === "BEFORE" ? "preTasks" : "postTasks"].push(c);
              return p;
            },
            { preTasks: [], postTasks: [] },
          ) ?? { preTasks: [], postTasks: [] };

          return (
            <Td borderRightWidth={1} key={lesson.id}>
              <HStack>
                {preTasks.map((preTask, index) => (
                  <QuizProgressItem
                    key={`pre_${preTask.id}`}
                    quiz={preTask}
                    index={index + 1}
                    submissions={student.submissions}
                  />
                ))}
                {lesson.exams?.map((exam) => (
                  <ExerciseProgressItem
                    key={exam.id}
                    exam={exam}
                    homeworks={student.homeworks}
                  />
                ))}
                {postTasks.map((postTask, index) => (
                  <QuizProgressItem
                    key={`post_${postTask.id}`}
                    quiz={postTask}
                    index={index + 1}
                    submissions={student.submissions}
                  />
                ))}
              </HStack>
            </Td>
          );
        });
    },
    [modules],
  );

  if (isLoading) return <Spinner color="primary.DEFAULT" />;

  if (isError)
    return (
      <ErrorSection
        retry={() => refetch?.({ page: pagination?.number ?? 0 })}
      />
    );

  if (!progresses.length) return <NoItemsCard />;

  return (
    <TableContainer>
      <Table __css={{ width: "full" }} variant="simple" bg="white">
        <Thead>
          <Tr>
            <Th
              position="sticky"
              left={0}
              zIndex={1}
              borderRightWidth={1}
              bg="white"
            />
            {Object.entries(modules).map(([moduleName, lessons]) => (
              <Th
                borderRightWidth={1}
                textAlign="center"
                key={moduleName}
                colSpan={lessons.length}
              >
                {moduleName}
              </Th>
            ))}
          </Tr>
          <Tr>
            <Th
              position="sticky"
              left={0}
              bg="white"
              zIndex={1}
              borderRightWidth={1}
            >
              {t("tables.students")}
            </Th>
            {Object.values(modules)
              .flat()
              .map((lesson) => (
                <Th textAlign="center" key={lesson.id} borderRightWidth={1}>
                  {lesson.name}
                </Th>
              ))}
          </Tr>
        </Thead>
        <Tbody>
          {progresses.map((student) => (
            <Tr key={student.id}>
              <Td
                position="sticky"
                left={0}
                bg="white"
                zIndex={1}
                borderRightWidth={1}
                fontWeight={600}
              >
                {student.fullName}
              </Td>
              {renderLearningPathLessons(student)}
            </Tr>
          ))}
        </Tbody>
      </Table>
      {pagination && (
        <Pagination
          pagination={pagination}
          goToPage={(page) => refetch?.({ page })}
        />
      )}
    </TableContainer>
  );
}
